import React, { useState, useMemo } from 'react';
import {
    Box,
    useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CommonCard from '../../../../components/card/CommonCard';
import ExerciseSummary from '../../../../components/exerciseSummary';
import BarChart from '../../../../components/charts/barChart';
import ChartSection from '../../../../components/charts/chartSection';
import DataItem from '../../../../components/dataItem';
import ProgressBar from '../../../../components/charts/progressBar';
import Divider from '../../../../components/divider';
import WeekCalendar from '../../../../components/charts/donutChart/weeklyCalendar';
import DateIndicator from '../../../../components/calendar/DateIndicator';
import StatsSection from '../../../../components/statsSection';
import ChartSectionBar from '../../../../components/charts/barChart/StatisticsChart';
import { EXERCISE_TARGETS } from '../../../../constants';
import { calculatePercentage, formatDateTime, formatDateTimeDisplay } from '../../../../utils/base';
import Toggle from '../../../../components/toggle';

export const StepTab = ({ physicalRecords }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });
    const [, setLoading] = useState(true);
    void setLoading;
    const [activeView, setActiveView] = useState(0); // 0: 일간, 1: 주간, 2: 월간, 3: 연간

    // 주간 데이터 상태 관리
    const [currentWeekIndex, setCurrentWeekIndex] = useState(0); // 현재 주간 인덱스
    const [selectedBarIndex, setSelectedBarIndex] = useState(-1); // 선택된 바 상태 관리

    // 주간 데이터 생성 (선택된 날짜 기준)
    const generateThisWeek = () => {
        // 선택된 날짜를 기반으로 주간 계산
        const currentDay = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Get start of week (Sunday) based on selectedDate
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - currentDay);
        startOfWeek.setHours(0, 0, 0, 0);

        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
        const week = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            date.setHours(0, 0, 0, 0);

            // 날짜 비교 시 시간 부분 제거
            const dateForCompare = new Date(date);
            dateForCompare.setHours(0, 0, 0, 0);
            const selectedDateForCompare = new Date(selectedDate);
            selectedDateForCompare.setHours(0, 0, 0, 0);
            const isSelected = dateForCompare.getTime() === selectedDateForCompare.getTime();

            // 해당 날짜의 실제 데이터 찾기 (로컬 시간대 사용)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const dayRecords = physicalRecords?.filter(record =>
                record.date && record.date.startsWith(dateStr)
            );

            // 데이터 합산
            const dayTotals = dayRecords?.reduce((acc, record) => {
                return {
                    totalSteps: acc.totalSteps + (record.totalSteps || 0),
                    calories: acc.calories + (record.calories || 0),
                    totalTime: acc.totalTime + (record.totalTime || 0)
                };
            }, {
                totalSteps: 0,
                calories: 0,
                totalTime: 0
            });

            const chartData = [
                {
                    label: t('didim'),
                    value: calculatePercentage(
                        dayTotals?.totalSteps,
                        EXERCISE_TARGETS.STEPS
                    ),
                    color: theme.palette.figma.steps,
                    radius: 25,
                    strokeWidth: 5,
                },
                {
                    label: t('calories'),
                    value: calculatePercentage(
                        dayTotals?.calories,
                        EXERCISE_TARGETS.CALORIES
                    ),
                    color: theme.palette.figma.calorieRed,
                    radius: 20,
                    strokeWidth: 5,
                },
                {
                    label: t('time'),
                    value: calculatePercentage(
                        dayTotals?.totalTime,
                        EXERCISE_TARGETS.ACTIVITY_TIME
                    ),
                    color: theme.palette.figma.time,
                    radius: 15,
                    strokeWidth: 5,
                },
            ];

            week.push({
                day: dayNames[i],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
                totalSteps: dayTotals?.totalSteps,
                calories: dayTotals?.calories,
                totalTime: dayTotals?.totalTime,
                hourlySteps: dayRecords?.[0]?.hourlySteps,
                hourlyCalories: dayRecords?.[0]?.hourlyCalories,
                hourlyTimes: dayRecords?.[0]?.hourlyTimes
            });
        }

        return week;
    };

    const days = useMemo(() => generateThisWeek(), [selectedDate, physicalRecords, theme.palette.figma, t, generateThisWeek]);

    // 과거 데이터 생성 (1년치 고정) - selectedDayData용
    const allDaysData = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

        const days = [];

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364);
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);
            date.setHours(0, 0, 0, 0);

            // 해당 날짜의 실제 데이터 찾기 (로컬 시간대 사용)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const dayRecords = physicalRecords?.filter(record =>
                record.date && record.date.startsWith(dateStr)
            );

            // 데이터 합산
            const dayTotals = dayRecords?.reduce((acc, record) => {
                return {
                    totalSteps: acc.totalSteps + (record.totalSteps || 0),
                    calories: acc.calories + (record.calories || 0),
                    totalTime: acc.totalTime + (record.totalTime || 0)
                };
            }, {
                totalSteps: 0,
                calories: 0,
                totalTime: 0
            });

            const chartData = [
                {
                    label: t('didim'),
                    value: calculatePercentage(
                        dayTotals?.totalSteps,
                        EXERCISE_TARGETS.STEPS
                    ),
                    color: theme.palette.figma.steps,
                    radius: 25,
                    strokeWidth: 5,
                },
                {
                    label: t('calories'),
                    value: calculatePercentage(
                        dayTotals?.calories,
                        EXERCISE_TARGETS.CALORIES
                    ),
                    color: theme.palette.figma.calorieRed,
                    radius: 20,
                    strokeWidth: 5,
                },
                {
                    label: t('time'),
                    value: calculatePercentage(
                        dayTotals?.totalTime,
                        EXERCISE_TARGETS.ACTIVITY_TIME
                    ),
                    color: theme.palette.figma.time,
                    radius: 15,
                    strokeWidth: 5,
                },
            ];

            const dateForCompare = new Date(date);
            dateForCompare.setHours(0, 0, 0, 0);
            const selectedDateForCompare = new Date(selectedDate);
            selectedDateForCompare.setHours(0, 0, 0, 0);
            const isSelected = dateForCompare.getTime() === selectedDateForCompare.getTime();

            days.push({
                day: dayNames[date.getDay()],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
                totalSteps: dayTotals?.totalSteps,
                calories: dayTotals?.calories,
                totalTime: dayTotals?.totalTime,
                hourlySteps: dayRecords?.[0]?.hourlySteps,
                hourlyCalories: dayRecords?.[0]?.hourlyCalories,
                hourlyTimes: dayRecords?.[0]?.hourlyTimes
            });
        }

        return days; // 가장 오래된 날짜부터 오늘까지 순서 (오늘이 배열의 마지막 = 오른쪽 끝)
    }, [selectedDate, physicalRecords, theme.palette.figma, t]);

    // 선택된 날짜의 시간대별 데이터
    const selectedDayData = useMemo(() => {
        const selectedDay = allDaysData.find(day => formatDateTime(day.date, '/', ':', 0, 10) === formatDateTime(selectedDate, '/', ':', 0, 10));

        if (!selectedDay) {
            return {
                hourlySteps: new Array(24).fill(0),
                hourlyCalories: new Array(24).fill(0),
                hourlyTimes: new Array(24).fill(0),
            };
        }

        // hourlySteps 파싱
        let hourlyStepsArray = new Array(24).fill(0);
        if (selectedDay.hourlySteps) {
            if (typeof selectedDay.hourlySteps === 'string') {
                try {
                    hourlyStepsArray = JSON.parse(selectedDay.hourlySteps);
                } catch (e) {
                    console.error('hourlySteps 파싱 실패:', e);
                    hourlyStepsArray = new Array(24).fill(0);
                }
            } else if (Array.isArray(selectedDay.hourlySteps)) {
                hourlyStepsArray = selectedDay.hourlySteps;
            }
        }

        // hourlyCalories 파싱
        let hourlyCaloriesArray = new Array(24).fill(0);
        if (selectedDay.hourlyCalories) {
            if (typeof selectedDay.hourlyCalories === 'string') {
                try {
                    hourlyCaloriesArray = JSON.parse(selectedDay.hourlyCalories);
                } catch (e) {
                    console.error('hourlyCalories 파싱 실패:', e);
                    hourlyCaloriesArray = new Array(24).fill(0);
                }
            } else if (Array.isArray(selectedDay.hourlyCalories)) {
                hourlyCaloriesArray = selectedDay.hourlyCalories;
            }
        }

        // hourlyTimes 파싱
        let hourlyTimesArray = new Array(24).fill(0);
        if (selectedDay.hourlyTimes) {
            if (typeof selectedDay.hourlyTimes === 'string') {
                try {
                    hourlyTimesArray = JSON.parse(selectedDay.hourlyTimes);
                } catch (e) {
                    console.error('hourlyTimes 파싱 실패:', e);
                    hourlyTimesArray = new Array(24).fill(0);
                }
            } else if (Array.isArray(selectedDay.hourlyTimes)) {
                hourlyTimesArray = selectedDay.hourlyTimes;
            }
        }

        return {
            hourlySteps: hourlyStepsArray,
            hourlyCalories: hourlyCaloriesArray,
            hourlyTimes: hourlyTimesArray,
        };
    }, [allDaysData, selectedDate]);

    const handleDayClick = (date) => {
        if (date) {
            const newDate = new Date(date);
            newDate.setHours(0, 0, 0, 0);
            setSelectedDate(newDate);
        }
    };

    // 선택된 바를 해제하는 함수
    const clearSelectedBar = () => {
        setSelectedBarIndex(-1);
    };

    const handleWeekChange = (newDate) => {
        setSelectedDate(newDate);
    };

    // 현재 주간 데이터 가져오기 (stepUnion.js 로직 활용)
    const getCurrentWeekData = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        // 월요일부터 시작하도록 조정 (월요일 = 1, 일요일 = 0)
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 일요일이면 -6, 아니면 월요일까지의 오프셋
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        const weekData = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);

            // 실제 데이터에서 해당 날짜 찾기
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const actualRecord = physicalRecords?.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            weekData.push({
                day: dateStr,
                value: actualRecord?.totalSteps || 0,
                floors: actualRecord?.floors || 0,
                color: (actualRecord?.totalSteps || 0) >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return weekData;
    };

    // 현재 주간 통계 계산
    const calculateCurrentWeekStats = () => {
        const currentWeekData = getCurrentWeekData();
        if (currentWeekData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        // 해당 주간의 실제 데이터 가져오기
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));

        let totalSteps = 0;
        let totalDistance = 0;
        let totalCalories = 0;
        let totalFloors = 0;
        let goalDays = 0;
        let _actualDays = 0;

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);

            const actualRecord = physicalRecords?.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            if (actualRecord) {
                totalSteps += actualRecord.totalSteps || 0;
                totalDistance += actualRecord.distance || 0;
                totalCalories += actualRecord.calories || 0;
                totalFloors += actualRecord.floors || 0;
                _actualDays++;

                if ((actualRecord.totalSteps || 0) >= EXERCISE_TARGETS.STEPS) {
                    goalDays++;
                }
            }
        }

        const averageSteps = Math.round(totalSteps / 7);
        const averageDistance = Math.round((totalDistance / 7) * 100) / 100;
        const averageCalories = Math.round(totalCalories / 7);
        const averageFloors = Math.round((totalFloors / 7) * 10) / 10;

        return {
            value: averageSteps,
            distance: averageDistance,
            calories: averageCalories,
            floors: averageFloors,
            totalSteps: totalSteps,
            goalDays: goalDays,
            totalDays: 7
        };
    };

    // 현재 월간 데이터 가져오기
    const getCurrentMonthData = () => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        const year = targetMonth.getFullYear();
        const month = targetMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const monthData = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = formatDateTime(date, '-', ':', 0, 10);

            const actualRecord = physicalRecords?.find(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 10);
                return recordDate === dateStr;
            });

            monthData.push({
                day: dateStr,
                value: actualRecord?.totalSteps || 0,
                floors: actualRecord?.floors || 0,
                color: (actualRecord?.totalSteps || 0) >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return monthData;
    };

    // 현재 연간 데이터 가져오기
    const getCurrentYearData = () => {
        const today = new Date();
        const targetYear = today.getFullYear() + currentYearIndex;

        const yearData = [];
        for (let month = 0; month < 12; month++) {
            const monthStr = `${targetYear}-${String(month + 1).padStart(2, '0')}`;

            // 해당 월의 모든 데이터 합산
            const monthRecords = physicalRecords?.filter(record => {
                const recordDate = formatDateTime(new Date(record.date), '-', ':', 0, 7);
                return recordDate === monthStr;
            });

            const monthTotals = monthRecords?.reduce((acc, record) => {
                return {
                    totalSteps: acc.totalSteps + (record.totalSteps || 0),
                    totalFloors: acc.totalFloors + (record.floors || 0),
                    goalDays: acc.goalDays + ((record.totalSteps || 0) >= EXERCISE_TARGETS.STEPS ? 1 : 0)
                };
            }, { totalSteps: 0, totalFloors: 0, goalDays: 0 });

            const averageSteps = monthTotals ? Math.round(monthTotals.totalSteps / 30) : 0;

            yearData.push({
                day: `${targetYear}-${String(month + 1).padStart(2, '0')}`,
                value: averageSteps,
                stepsTotal: monthTotals?.totalSteps || 0,
                floors: monthTotals?.totalFloors || 0,
                goalDays: monthTotals?.goalDays || 0,
                color: averageSteps >= EXERCISE_TARGETS.STEPS ? theme.palette.figma.steps : theme.palette.figma.white[40]
            });
        }
        return yearData;
    };

    // 현재 월간 통계 계산
    const calculateCurrentMonthStats = () => {
        const monthData = getCurrentMonthData();
        if (monthData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0
            };
        }

        const totals = monthData.reduce((acc, day) => {
            acc.totalSteps += day.value;
            acc.totalFloors += day.floors;
            if (day.value >= EXERCISE_TARGETS.STEPS) {
                acc.goalDays++;
            }
            return acc;
        }, { totalSteps: 0, totalFloors: 0, goalDays: 0 });

        const averageSteps = Math.round(totals.totalSteps / monthData.length);
        const averageFloors = Math.round((totals.totalFloors / monthData.length) * 10) / 10;

        return {
            value: averageSteps,
            distance: 0, // 월간에서는 거리 계산 생략
            calories: 0, // 월간에서는 칼로리 계산 생략
            floors: averageFloors,
            totalSteps: totals.totalSteps,
            goalDays: totals.goalDays,
            totalDays: monthData.length
        };
    };

    // 현재 연간 통계 계산
    const calculateCurrentYearStats = () => {
        const yearData = getCurrentYearData();
        if (yearData.length === 0) {
            return {
                value: 0,
                distance: 0,
                calories: 0,
                floors: 0,
                totalSteps: 0,
                goalDays: 0,
                totalDays: 0,
                bestDay: 0,
                worstDay: 0
            };
        }

        const totals = yearData.reduce((acc, month) => {
            acc.totalSteps += month.stepsTotal;
            acc.totalFloors += month.floors;
            acc.goalDays += month.goalDays;
            return acc;
        }, { totalSteps: 0, totalFloors: 0, goalDays: 0 });

        const averageSteps = Math.round(totals.totalSteps / 12);
        const averageFloors = Math.round((totals.totalFloors / 12) * 10) / 10;

        const bestMonth = yearData.reduce((best, month) =>
            month.value > best.value ? month : best, yearData[0]);
        const worstMonth = yearData.reduce((worst, month) =>
            month.value < worst.value ? month : worst, yearData[0]);

        return {
            value: averageSteps,
            distance: 0, // 연간에서는 거리 계산 생략
            calories: 0, // 연간에서는 칼로리 계산 생략
            floors: averageFloors,
            totalSteps: totals.totalSteps,
            goalDays: totals.goalDays,
            totalDays: 12,
            bestDay: bestMonth.value,
            worstDay: worstMonth.value
        };
    };

    // 주간 제목 생성 (다국어 지원)
    const getWeeklyTitle = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset + (currentWeekIndex * 7));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const startFormatted = formatDateTimeDisplay(startOfWeek, 'monthDay');
        const endFormatted = formatDateTimeDisplay(endOfWeek, 'monthDay');
        return `${startFormatted} - ${endFormatted}`;
    };

    // 월간 제목 생성 (다국어 지원)
    const getMonthlyTitle = () => {
        const today = new Date();
        const targetMonth = new Date(today);
        targetMonth.setMonth(today.getMonth() + currentMonthIndex);

        return formatDateTimeDisplay(targetMonth, 'yearMonth');
    };

    // 연간 제목 생성 (다국어 지원)
    const getYearlyTitle = () => {
        const today = new Date();
        const targetYear = new Date(today.getFullYear() + currentYearIndex, 0, 1);
        return formatDateTimeDisplay(targetYear, 'year');
    };

    // 월간/연간 인덱스 상태 추가
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
    const [currentYearIndex, setCurrentYearIndex] = useState(0);

    // 주간 이동 함수들
    const handlePreviousWeek = () => {
        setCurrentWeekIndex(currentWeekIndex - 1);
        clearSelectedBar();
    };

    const handleNextWeek = () => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startOfWeek.setDate(today.getDate() + mondayOffset);

        const nextWeekStart = new Date(startOfWeek);
        nextWeekStart.setDate(startOfWeek.getDate() + (currentWeekIndex + 1) * 7);

        if (nextWeekStart <= today) {
            setCurrentWeekIndex(currentWeekIndex + 1);
            clearSelectedBar();
        }
    };

    // 월간 이동 함수들
    const handlePreviousMonth = () => {
        setCurrentMonthIndex(currentMonthIndex - 1);
        clearSelectedBar();
    };

    const handleNextMonth = () => {
        const today = new Date();
        const currentMonth = new Date(today);
        currentMonth.setMonth(today.getMonth() + currentMonthIndex + 1);

        if (currentMonth <= today) {
            setCurrentMonthIndex(currentMonthIndex + 1);
            clearSelectedBar();
        }
    };

    // 연간 이동 함수들
    const handlePreviousYear = () => {
        setCurrentYearIndex(currentYearIndex - 1);
        clearSelectedBar();
    };

    const handleNextYear = () => {
        const today = new Date();
        const currentYear = new Date(today);
        currentYear.setFullYear(today.getFullYear() + currentYearIndex + 1);

        if (currentYear <= today) {
            setCurrentYearIndex(currentYearIndex + 1);
            clearSelectedBar();
        }
    };

    const exerciseData = useMemo(() => {
        if (!physicalRecords || physicalRecords.length === 0) {
            return {
                totalSteps: 0,
                calories: 0,
                totalTime: 0,
                distance: 0
            };
        }

        // 선택된 날짜의 데이터 찾기 (로컬 시간대 사용)
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const selectedDateStr = `${year}-${month}-${day}`;
        const dayRecords = physicalRecords?.filter(record =>
            record.date && record.date.startsWith(selectedDateStr)
        );

        // 데이터 합산
        const totals = dayRecords?.reduce((acc, record) => {
            return {
                totalSteps: acc.totalSteps + (record.totalSteps || 0),
                calories: acc.calories + (record.calories || 0),
                totalTime: acc.totalTime + (record.totalTime || 0),
                distance: acc.distance + (record.distance || 0)
            };
        }, {
            totalSteps: 0,
            calories: 0,
            totalTime: 0,
            distance: 0
        });

        return totals;
    }, [physicalRecords, selectedDate]);


    // 주간/월간/연간 뷰 렌더링
    const renderRangeView = () => {
        let stats, chartData, yAxisData, title, activeViewStr, onPrevious, onNext;

        if (activeView === 1) {
            // 주간 뷰
            stats = calculateCurrentWeekStats();
            chartData = getCurrentWeekData().map(item => ({
                day: item.day,
                value: item.value,
                floors: item.floors,
                color: item.color
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentWeekData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentWeekData().map(item => item.value), 1) / 2, Math.max(...getCurrentWeekData().map(item => item.value), 1)]
            };
            title = getWeeklyTitle();
            activeViewStr = "week";
            onPrevious = handlePreviousWeek;
            onNext = handleNextWeek;
        } else if (activeView === 2) {
            // 월간 뷰
            stats = calculateCurrentMonthStats();
            chartData = getCurrentMonthData().map(item => ({
                day: item.day,
                value: item.value,
                floors: item.floors,
                color: item.color
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentMonthData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentMonthData().map(item => item.value), 1) / 2, Math.max(...getCurrentMonthData().map(item => item.value), 1)]
            };
            title = getMonthlyTitle();
            activeViewStr = "month";
            onPrevious = handlePreviousMonth;
            onNext = handleNextMonth;
        } else if (activeView === 3) {
            // 연간 뷰
            stats = calculateCurrentYearStats();
            chartData = getCurrentYearData().map(item => ({
                day: item.day,
                value: item.value,
                floors: item.floors,
                color: item.color,
                stepsTotal: item.stepsTotal,
                goalDays: item.goalDays
            }));
            yAxisData = {
                maxValue: Math.max(...getCurrentYearData().map(item => item.value), 1),
                minValue: 0,
                averageValue: stats.value,
                yAxisValues: [0, Math.max(...getCurrentYearData().map(item => item.value), 1) / 2, Math.max(...getCurrentYearData().map(item => item.value), 1)]
            };
            title = getYearlyTitle();
            activeViewStr = "year";
            onPrevious = handlePreviousYear;
            onNext = handleNextYear;
        }

        return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* 네비게이션 */}
                <DateIndicator
                    title={title}
                    activeView={activeViewStr}
                    onPreviousWeek={onPrevious}
                    onPreviousMonth={onPrevious}
                    onPreviousYear={onPrevious}
                    onNextWeek={onNext}
                    onNextMonth={onNext}
                    onNextYear={onNext}
                />

                {/* 메인 콘텐츠 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* 좌측 요약 패널 */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <CommonCard>
                            {/* 평균 걸음수 */}
                            <Box sx={{ width: '100%' }}>
                                <DataItem
                                    value={stats?.value?.toLocaleString()}
                                    unit={t('didim_day')}
                                    size="large"
                                    sx={{ justifyContent: "left" }}
                                />
                            </Box>

                            {/* 통계 섹션 */}
                            <StatsSection
                                stats={{
                                    distance: stats?.distance,
                                    calories: stats?.calories,
                                    floors: stats?.floors,
                                    totalSteps: stats?.totalSteps,
                                    goalDays: stats?.goalDays,
                                    totalDays: stats?.totalDays,
                                    bestDay: stats?.bestDay,
                                    worstDay: stats?.worstDay
                                }}
                                activeView={activeViewStr}
                            />
                        </CommonCard>
                    </Box>

                    {/* 우측 바 차트 */}
                    <Box sx={{ flex: 1 }}>
                        <CommonCard>
                            <ChartSectionBar
                                activeView={activeViewStr}
                                chartData={chartData}
                                yAxisData={yAxisData}
                                selectedBarIndex={selectedBarIndex}
                                onBarSelect={setSelectedBarIndex}
                            />
                        </CommonCard>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 상단 뷰 선택기 */}
            <Toggle
                options={[t('daily'), t('weekly'), t('monthly'), t('yearly')]}
                value={activeView}
                onChange={(newView) => {
                    setActiveView(newView);
                    clearSelectedBar();
                }}
            />

            {/* 뷰별 콘텐츠 렌더링 */}
            {activeView === 0 ? (
                <>
                    {/* 일별 날짜 선택기 with week swipe */}
                    <WeekCalendar
                        days={days}
                        onDayClick={handleDayClick}
                        size="large"
                        enableWeekSwipe={true}
                        onWeekChange={handleWeekChange}
                        selectedDate={selectedDate}
                        enableScroll={true}
                        allDaysData={allDaysData}
                        isLoading={false}
                        onScrollToEnd={(direction) => {
                            // 현재는 physicalRecords가 prop으로 전달되므로 상위 컴포넌트에서 처리
                        }}
                    />

                    {/* 메인 콘텐츠 */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* 좌측 메인 영역 */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* 상단 카드 - 일일 활동 요약 */}
                            <CommonCard>
                                <ExerciseSummary
                                    totalSteps={exerciseData?.totalSteps}
                                    calories={exerciseData?.calories}
                                    totalTime={exerciseData?.totalTime}
                                />

                                <Divider
                                    lineColor={theme.palette.figma.white[20]}
                                />

                                {/* Additional Stats */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '5px',
                                        width: '100%',
                                    }}
                                >
                                    <DataItem
                                        label={t('distance_by_activity')}
                                        value={exerciseData?.distance}
                                        unit="km"
                                    />
                                </Box>
                            </CommonCard>

                            {/* 하단 카드 - 걸음수 진행률 */}
                            <CommonCard>
                                {/* 걸음수 숫자 */}
                                <DataItem value={exerciseData?.totalSteps} unit={t('didim')} size="large" sx={{ justifyContent: "center" }} />

                                {/* 진행률 바 */}
                                <ProgressBar
                                    current={exerciseData?.totalSteps}
                                    rangeStart={0}
                                    rangeEnd={EXERCISE_TARGETS.STEPS}
                                    start={0}
                                    end={exerciseData?.totalSteps}
                                    height="30px"
                                    borderRadius="20px"
                                    progressColor={theme.palette.figma.steps}
                                    showCurrentIndicator={false}
                                    showProgress={true}
                                    showStartEndValues={true}
                                    startValue={0}
                                    endValue={`${t('goal')}${EXERCISE_TARGETS.STEPS.toLocaleString()}`}
                                />

                                {/* 거리와 칼로리 */}
                                <Box sx={{
                                    display: 'flex',
                                    padding: '10px 0',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    alignSelf: 'stretch'
                                }}>
                                    {/* 거리 */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '5px',
                                        flex: '1 0 0',
                                        borderRight: `1px solid ${theme.palette.figma.white[20]}`
                                    }}>
                                        <DataItem value={exerciseData?.distance} unit="km" size="medium" sx={{ justifyContent: "center" }} />
                                    </Box>

                                    {/* 칼로리 */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: '5px',
                                        flex: '1 0 0',
                                    }}>
                                        <DataItem value={exerciseData?.calories} unit="kcal" size="medium" sx={{ justifyContent: "center" }} />
                                    </Box>
                                </Box>
                            </CommonCard>
                        </Box>

                        {/* 우측 그래프 영역 */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* 시간대별 디딤수 그래프 */}
                            <CommonCard sx={{ gap: '20px' }}>
                                {/* Steps Chart */}
                                <ChartSection
                                    title={t('step_count')}
                                    renderChart={<BarChart data={selectedDayData?.hourlySteps || new Array(24).fill(0)} color={theme.palette.figma.steps} />}
                                />

                                <Divider
                                    lineColor={theme.palette.figma.white[20]}
                                />

                                {/* Calories Chart */}
                                <ChartSection
                                    title={t('activity_calories')}
                                    renderChart={<BarChart data={selectedDayData?.hourlyCalories || new Array(24).fill(0)} color={theme.palette.figma.calorieRed} />}
                                />

                                <Divider
                                    lineColor={theme.palette.figma.white[20]}
                                />

                                {/* Time Chart */}
                                <ChartSection
                                    title={t('activity_time')}
                                    renderChart={<BarChart data={selectedDayData?.hourlyTimes || new Array(24).fill(0)} color={theme.palette.figma.time} />}
                                />
                            </CommonCard>
                        </Box>
                    </Box>
                </>
            ) : (renderRangeView()
            )}
        </Box>
    );
};


export default StepTab;
